import React from 'react';
import { ClassName, Disabled } from 'interfaces/shared';
import { StyledCollapse, StyledErrorCollapse, StyledWarningCollapse } from './style';
import { Icon } from '../icon';
import type { CollapsibleType } from 'antd/es/collapse/CollapsePanel';

export interface PanelProps {
  isActive?: boolean;
  header?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  showArrow?: boolean;
  forceRender?: boolean;
  extra?: React.ReactNode;
  collapsible?: CollapsibleType;
}

export interface CollapseProps extends Disabled, ClassName {
  accordion?: boolean;
  children?: React.ReactNode;
  ghost?: boolean;
  defaultActiveKey?: string[] | string | number[] | number;
  onChange?: (key: string | string[]) => void;
  error?: boolean;
  warning?: boolean;
  expandIcon?: React.ReactNode | ((panelProps: PanelProps) => React.ReactNode);
  collapsible?: 'header' | 'icon' | 'disabled';
  expandIconPosition?: 'start' | 'end';
}

export const Collapse = ({
  accordion,
  children,
  ghost,
  error,
  warning,
  defaultActiveKey,
  onChange,
  expandIcon,
  collapsible,
  expandIconPosition = 'start',
  className,
}: CollapseProps) => {
  if (error) {
    return (
      <StyledErrorCollapse
        accordion={accordion}
        expandIconPosition={expandIconPosition}
        expandIcon={(panelProps: PanelProps) => {
          if (expandIcon) {
            return typeof expandIcon === 'function' ? expandIcon(panelProps) : expandIcon;
          } else {
            if (panelProps.isActive) {
              return expandIconPosition === 'end' ? (
                <Icon icon="angle-up" faBase="far" />
              ) : (
                <Icon icon="angle-down" faBase="far" />
              );
            }
            return expandIconPosition === 'end' ? (
              <Icon icon="angle-down" faBase="far" />
            ) : (
              <Icon icon="angle-right" faBase="far" />
            );
          }
        }}
        ghost={ghost}
        collapsible={collapsible}
        className={className}
      >
        {children}
      </StyledErrorCollapse>
    );
  }

  if (warning) {
    return (
      <StyledWarningCollapse
        accordion={accordion}
        expandIconPosition={expandIconPosition}
        expandIcon={(panelProps: PanelProps) => {
          if (expandIcon) {
            return typeof expandIcon === 'function' ? expandIcon(panelProps) : expandIcon;
          } else {
            if (panelProps.isActive) {
              return expandIconPosition === 'end' ? (
                <Icon icon="angle-up" faBase="far" />
              ) : (
                <Icon icon="angle-down" faBase="far" />
              );
            }
            return expandIconPosition === 'end' ? (
              <Icon icon="angle-down" faBase="far" />
            ) : (
              <Icon icon="angle-right" faBase="far" />
            );
          }
        }}
        ghost={ghost}
      >
        {children}
      </StyledWarningCollapse>
    );
  }

  return (
    <StyledCollapse
      accordion={accordion}
      defaultActiveKey={defaultActiveKey}
      onChange={onChange}
      expandIconPosition={expandIconPosition}
      expandIcon={(panelProps: PanelProps) => {
        if (expandIcon) {
          return typeof expandIcon === 'function' ? expandIcon(panelProps) : expandIcon;
        } else {
          if (panelProps.isActive) {
            return expandIconPosition === 'end' ? (
              <Icon icon="angle-up" faBase="far" />
            ) : (
              <Icon icon="angle-down" faBase="far" />
            );
          }
          return expandIconPosition === 'end' ? (
            <Icon icon="angle-down" faBase="far" />
          ) : (
            <Icon icon="angle-right" faBase="far" />
          );
        }
      }}
      ghost={ghost}
      className={className}
    >
      {children}
    </StyledCollapse>
  );
};
